import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import 'react-toastify/dist/ReactToastify.css';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';

interface AddSeasonModalProps {
  onClose: () => void;
  onSeasonAdded?: () => void;
  editSeason?: any | null;
}

// Zod validation schema
const seasonSchema = z
  .object({
    name: z.string().min(1, 'Season name is required').trim(),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    halfPeriod: z.boolean(),
    shotClock: z.boolean(),
    active: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endDate);
        return startDate < endDate;
      }
      return true;
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    },
  );

type SeasonFormData = z.infer<typeof seasonSchema>;

const AddSeasonModal: React.FC<AddSeasonModalProps> = ({ onClose, onSeasonAdded, editSeason }) => {
  const clientIdFromSession = sessionManager.getClientId();
  const [isLoading, setIsLoading] = useState(false);

  // Initialize react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SeasonFormData>({
    resolver: zodResolver(seasonSchema),
    defaultValues: {
      name: '',
      startDate: '',
      endDate: '',
      halfPeriod: false,
      shotClock: false,
      active: true,
    },
  });

  // Pre-fill form if editing
  useEffect(() => {
    if (editSeason) {
      reset({
        name: editSeason.name || '',
        startDate: editSeason.startDate ? new Date(editSeason.startDate).toISOString().split('T')[0] : '',
        endDate: editSeason.endDate ? new Date(editSeason.endDate).toISOString().split('T')[0] : '',
        halfPeriod: !!editSeason.halfPeriod,
        shotClock: !!editSeason.shotClock,
        active: !!editSeason.active,
      });
    } else {
      reset({
        name: '',
        startDate: '',
        endDate: '',
        halfPeriod: false,
        shotClock: false,
        active: true,
      });
    }
  }, [editSeason, reset]);

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: SeasonFormData) => {
    // Check if user is authenticated
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    setIsLoading(true);

    try {
      let response;
      if (editSeason && editSeason._id) {
        // Update season
        const updateData = {
          ...data,
          _id: editSeason._id,
        };
        response = await api.put(API_URLS.UPDATE_SEASON, updateData);
        if (response.data.success) {
          showSuccessMsg('Season updated successfully!');
          onSeasonAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update season');
        }
      } else {
        // Add season
        const submitData = {
          ...data,
          clientId: clientIdFromSession,
        };
        response = await api.post(API_URLS.ADD_SEASON, submitData);

        if (response.data.success) {
          showSuccessMsg('Season added successfully!');
          onSeasonAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to add season');
        }
      }
    } catch (error: any) {
      console.error('Error adding/updating season:', error);

      // Handle different types of errors
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add/update season. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={handleClose} maxWidth="md" maxHeight="90vh" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editSeason ? 'Edit Season' : 'Add Season'}</ModalTitle>

      <ModalContent>
        <form id="season-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Season Name *</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter season name"
              disabled={isLoading || isSubmitting}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Start Date *</label>
            <input
              type="date"
              {...register('startDate')}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.startDate ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading || isSubmitting}
            />
            {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">End Date *</label>
            <input
              type="date"
              {...register('endDate')}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.endDate ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading || isSubmitting}
            />
            {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate.message}</p>}
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('halfPeriod')}
                className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                disabled={isLoading || isSubmitting}
              />
              <span className="text-gray-700">Use Halves (uncheck for Quarters)</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('shotClock')}
                className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                disabled={isLoading || isSubmitting}
              />
              <span className="text-gray-700">Shot Clock</span>
            </label>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('active')}
                className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                disabled={isLoading || isSubmitting}
              />
              <span className="text-gray-700">Active</span>
            </label>
          </div>
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="season-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editSeason ? 'Saving...' : 'Adding...'}
            </>
          ) : editSeason ? (
            'Save Changes'
          ) : (
            'Add Season'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddSeasonModal;
